import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { useEffect, useRef } from 'react';

/**
 * Prefetches critical data after login to eliminate "click and wait" delays.
 * 
 * Runs once when user transitions from unauthenticated → authenticated.
 * Prefetches:
 *   - Client/practitioner profile (depending on role)
 *   - Dashboard essential data
 */
export function useAuthPrefetch() {
  const { user, activeRole, authStatus, roleStatus } = useAuth();
  const queryClient = useQueryClient();
  const hasPrefetched = useRef<string | null>(null);

  useEffect(() => {
    if (authStatus !== 'loggedIn' || roleStatus !== 'resolved' || !user || !activeRole) return;
    // Only prefetch once per user session
    if (hasPrefetched.current === user.id) return;
    hasPrefetched.current = user.id;

    const prefetch = async () => {
      console.log('[Prefetch] Starting prefetch for', activeRole);

      if (activeRole === 'client') {
        // Prefetch client profile
        queryClient.prefetchQuery({
          queryKey: ['client-profile', user.id],
          queryFn: async () => {
            const { data } = await supabase
              .from('clients')
              .select('*')
              .eq('id', user.id)
              .maybeSingle();
            return data;
          },
          staleTime: 5 * 60 * 1000,
        });

        // Prefetch saved practitioners list (just IDs)
        queryClient.prefetchQuery({
          queryKey: ['saved-practitioners', user.id],
          queryFn: async () => {
            const { data } = await supabase
              .from('client_saved_practitioners')
              .select('practitioner_id')
              .eq('client_id', user.id);
            return data || [];
          },
          staleTime: 2 * 60 * 1000,
        });
      }

      if (activeRole === 'practitioner') {
        // Prefetch practitioner profile
        queryClient.prefetchQuery({
          queryKey: ['practitioner-profile', user.id],
          queryFn: async () => {
            const { data } = await supabase
              .from('practitioners')
              .select('*')
              .eq('id', user.id)
              .maybeSingle();
            return data;
          },
          staleTime: 5 * 60 * 1000,
        });

        // Prefetch practitioner services
        queryClient.prefetchQuery({
          queryKey: ['practitioner-services', user.id],
          queryFn: async () => {
            const { data } = await supabase
              .from('services')
              .select('*')
              .eq('practitioner_id', user.id)
              .eq('is_active', true)
              .order('created_at', { ascending: false });
            return data || [];
          },
          staleTime: 2 * 60 * 1000,
        });
      }
    };

    prefetch();
  }, [authStatus, roleStatus, user, activeRole, queryClient]);
}
